import { EntityEnum } from '@goldcast/api/backend';
import { useSyncExternalStore } from 'react';
import createStore from '@/stores/store';
import { isEntitySharedVideo, isEntityUserFeed } from '@/libs/activityUtils';

export enum RecordingVideoType {
  SCREENSHARE = 'SCREENSHARE',
  USER_FEED = 'USER_FEED',
  SHARED_VIDEO = 'SHARED_VIDEO'
}

export type VideoToSpeakerDetails = Record<
  string,
  {
    speakerKey: string;
    videoType: RecordingVideoType;
  }
>;
export type BroadcastToVideoSpeakerDetailsStore = Record<string, VideoToSpeakerDetails>;

const videoSpeakerDetailsStore = createStore<BroadcastToVideoSpeakerDetailsStore>({});

export const useVideoDetails = () => {
  const videoDetailsSnapshot = useSyncExternalStore(
    videoSpeakerDetailsStore.subscribe,
    videoSpeakerDetailsStore.getSnapshot
  );
  return videoDetailsSnapshot;
};

export const getVideoDetailsForBroadcast = ({
  broadcastId,
  snapshot
}: {
  broadcastId: string;
  snapshot?: BroadcastToVideoSpeakerDetailsStore;
}) => {
  return snapshot?.[broadcastId] ?? videoSpeakerDetailsStore.getSnapshot()?.[broadcastId] ?? {};
};

export const updateVideoToSpeakerDetailsStore = ({
  broadcastId,
  updatedSpeakerDetails
}: {
  broadcastId: string;
  updatedSpeakerDetails: VideoToSpeakerDetails;
}) => {
  videoSpeakerDetailsStore.update(prev => {
    return {
      ...prev,
      [broadcastId]: updatedSpeakerDetails
    };
  });
};

export const getRecordingSpeakerKey = ({
  broadcastId,
  recordingId,
  snapshot
}: {
  broadcastId: string;
  recordingId: string;
  snapshot?: BroadcastToVideoSpeakerDetailsStore;
}) => {
  const recordingSpeakerDetails = getVideoDetailsForBroadcast({ broadcastId, snapshot })?.[recordingId];
  if (!recordingSpeakerDetails) {
    return null;
  }
  return recordingSpeakerDetails.speakerKey;
};

export const isRecordingUserFeed = ({
  broadcastId,
  recordingId,
  snapshot
}: {
  broadcastId: string;
  recordingId: string;
  snapshot?: BroadcastToVideoSpeakerDetailsStore;
}) => {
  const recordingSpeakerDetails = getVideoDetailsForBroadcast({ broadcastId, snapshot })?.[recordingId];
  if (!recordingSpeakerDetails) {
    return false;
  }
  return recordingSpeakerDetails.videoType === RecordingVideoType.USER_FEED;
};

export const isRecordingScreenshare = ({
  broadcastId,
  recordingId,
  snapshot
}: {
  broadcastId: string;
  recordingId: string;
  snapshot?: BroadcastToVideoSpeakerDetailsStore;
}) => {
  const recordingSpeakerDetails = getVideoDetailsForBroadcast({ broadcastId, snapshot })?.[recordingId];
  if (!recordingSpeakerDetails) {
    return false;
  }
  return recordingSpeakerDetails.videoType === RecordingVideoType.SCREENSHARE;
};

export const doesRecordingHaveSpeaker = ({
  broadcastId,
  recordingId,
  snapshot
}: {
  broadcastId: string;
  recordingId: string;
  snapshot?: BroadcastToVideoSpeakerDetailsStore;
}) => {
  const speakerKey = getRecordingSpeakerKey({ broadcastId, recordingId, snapshot });
  return typeof speakerKey === 'string' && speakerKey.length > 0;
};

export const isRecordingSharedVideo = ({
  broadcastId,
  recordingId,
  snapshot
}: {
  broadcastId: string;
  recordingId: string;
  snapshot?: BroadcastToVideoSpeakerDetailsStore;
}) => {
  const recordingSpeakerDetails = getVideoDetailsForBroadcast({ broadcastId, snapshot })?.[recordingId];
  if (!recordingSpeakerDetails) {
    return false;
  }
  return recordingSpeakerDetails.videoType === RecordingVideoType.SHARED_VIDEO;
};

export const getVideoTypeFromActivityEntity = (entity: EntityEnum): RecordingVideoType => {
  if (isEntityUserFeed(entity)) {
    return RecordingVideoType.USER_FEED;
  }
  if (isEntitySharedVideo(entity)) {
    return RecordingVideoType.SHARED_VIDEO;
  }
  return RecordingVideoType.SCREENSHARE;
};
